import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { getS3AccessToken } from '../../api/setting/setting';
import type { S3AccessTokenData } from '../../api/setting/setting';

interface UploadState {
  // S3上传配置信息
  s3Config: {
    // 是否正在加载配置
    loading: boolean;
    // 上传主机地址
    host?: string;
    // 访问密钥ID
    accessKeyId?: string;
    // 过期时间
    expire?: number;
    // 签名
    signature?: string;
    // 策略
    policy?: string;
    // 目录
    directory?: string;
    // 回调
    callback?: string;
    // 错误信息
    error?: string;
  };
  // 上传进度信息
  progress: {
    // 是否正在上传
    uploading: boolean;
    // 上传进度百分比
    percent: number;
    // 已上传字节数
    loaded: number;
    // 总字节数
    total: number;
    // 当前上传的文件信息
    currentFile?: {
      name: string;
      size: number;
      type: string;
    };
    // 错误信息
    error?: string;
  };
}

// 初始状态
const initialState: UploadState = {
  s3Config: {
    loading: false,
  },
  progress: {
    uploading: false,
    percent: 0,
    loaded: 0,
    total: 0,
  },
};

/**
 * 检查当前S3配置是否有效（存在且未过期）
 * @param state 当前状态
 * @returns 是否有效
 */
const isS3ConfigValid = (state: UploadState): boolean => {
  const { host, accessKeyId, expire } = state.s3Config;
  
  // 检查必要字段是否存在
  if (!host || !accessKeyId || !expire) {
    return false;
  }
  
  // 检查是否过期（提前5分钟判断过期，避免边界问题）
  const now = Date.now();
  const expireTime = expire * 1000; // 转换为毫秒
  const bufferTime = 5 * 60 * 1000; // 5分钟缓冲期
  
  return now < (expireTime - bufferTime);
};

/**
 * 获取S3上传配置的异步Thunk
 */
export const fetchS3Config = createAsyncThunk(
  'utils/fetchS3Config',
  async (_, { rejectWithValue }) => {
    try {
      const params = {
        shopDomain: shopify.config.shop || ''
      }
      const response = await getS3AccessToken(params);
      console.log('fetchS3Configaaaaaaaaaaaaaaaaaa', response)
      if (!response) {
        return rejectWithValue('获取上传配置失败');
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : '获取上传配置失败');
    }
  }
);

const utilsSlice = createSlice({
  name: 'utils',
  initialState,
  reducers: {
    // 重置上传进度
    resetUploadProgress: (state) => {
      state.progress = initialState.progress;
    },
    // 更新上传进度
    updateUploadProgress: (state, action: PayloadAction<{
      percent: number;
      loaded: number;
      total: number;
    }>) => {
      state.progress.uploading = true;
      state.progress.percent = action.payload.percent;
      state.progress.loaded = action.payload.loaded;
      state.progress.total = action.payload.total;
    },
    // 设置当前上传文件信息
    setCurrentFile: (state, action: PayloadAction<{
      name: string;
      size: number;
      type: string;
    }>) => {
      state.progress.currentFile = action.payload;
    },
    // 上传成功
    uploadSuccess: (state) => {
      state.progress.uploading = false;
      state.progress.percent = 100;
      state.progress.error = undefined;
    },
    // 上传失败
    uploadFailed: (state, action: PayloadAction<string>) => {
      state.progress.uploading = false;
      state.progress.error = action.payload;
    },
    // 上传开始
    uploadStarted: (state) => {
      state.progress.uploading = true;
      state.progress.percent = 0;
      state.progress.loaded = 0;
      state.progress.total = 0;
      state.progress.error = undefined;
    },
    // 手动重置S3配置（用于强制刷新）
    resetS3Config: (state) => {
      state.s3Config = initialState.s3Config;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取S3配置开始
      .addCase(fetchS3Config.pending, (state) => {
        console.log('获取上传配置开始')
        state.s3Config.loading = true;
        state.s3Config.error = undefined;
      })
      // 获取S3配置成功
      .addCase(fetchS3Config.fulfilled, (state, action: PayloadAction<S3AccessTokenData>) => {
        console.log('获取上传配置成功', action.payload)
        state.s3Config.loading = false;
        state.s3Config.host = action.payload.host;
        state.s3Config.accessKeyId = action.payload.accessKeyId;
        state.s3Config.expire = action.payload.expire;
        state.s3Config.signature = action.payload.signature;
        state.s3Config.policy = action.payload.policy;
        state.s3Config.directory = action.payload.directory;
        state.s3Config.callback = action.payload.callback;
      })
      // 获取S3配置失败
      .addCase(fetchS3Config.rejected, (state, action) => {
        console.log('获取上传配置失败', action.payload)
        state.s3Config.loading = false;
        state.s3Config.error = action.payload as string;
      });
  }
});

// 导出操作
export const {
  resetUploadProgress,
  updateUploadProgress,
  setCurrentFile,
  uploadSuccess,
  uploadFailed,
  uploadStarted,
  resetS3Config
} = utilsSlice.actions;

// 导出选择器
export const selectS3Config = (state: RootState) => {
  return state.utils?.s3Config || initialState.s3Config
};
export const selectUploadProgress = (state: RootState) => {
  return state.utils?.progress || initialState.progress
};
export const selectIsS3ConfigValid = (state: RootState) => {
  return state.utils ? isS3ConfigValid(state.utils) : false
};

// 导出reducer
export default utilsSlice.reducer;
